import Link from 'next/link';

import { logger } from '@/lib/logger';

import { IssuedByCircleBadge } from '@/components/Badges/IssuedByCircleBadge';

import { fontStyles } from '@/styles/typography';

import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';

import { NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID } from '../../../constants/chain';
import { routes } from '../../../utils/routes';
import { AvatarSkeleton, PillSkeleton, TextSkeleton } from '../../Skeleton';

type TokenShortcutProps = {
  loading?: boolean;
  chainId?: number;
  title?: string;
  subtitle?: string;
  tokenAddress?: string;
  issuer?: string;
};

export function TokenProfileRelatedTokenShortcut({
  chainId,
  title,
  subtitle,
  loading = false,
  tokenAddress,
  issuer,
}: TokenShortcutProps) {
  const networkName = !!chainId && NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[chainId]?.toLowerCase();

  if (!networkName && !loading) {
    logger.warn('No network name for chainId', chainId);
    return null;
  }

  if (!tokenAddress && !loading) {
    logger.warn('No token address for chainId', chainId);
    return null;
  }

  const isNativeUsdc = issuer === 'circle';

  return (
    <Link
      href={{
        pathname: routes.TOKEN_PROFILE,
        query: {
          chainId: networkName || '',
          contractAddresses: [tokenAddress || '', ''],
        },
      }}
      className="bg-special-background-card shadow-10 grid relative w-fit items-center justify-start pl-2.5 pr-3 py-1 rounded-md border border-solid border-token-gray-300 min-w-[200px] [grid-template-columns:repeat(3,fit-content(33%))] [grid-template-rows:repeat(2,fit-content(33%))] mobile:gap-x-2 tablet:gap-x-0 no-underline data-[loading=false]:hover:border-token-gray-400 data-[loading=false]:hover:bg-token-gray-200 data-[loading=true]:pointer-events-none"
      data-loading={loading}
      tabIndex={loading ? -1 : undefined}
      aria-disabled={loading}
      onClick={() => {
        const app = document.getElementById('app');
        app?.scrollTo({ top: 0, behavior: 'smooth' });
      }}
    >
      {loading ? (
        <div className="w-8 h-8 self-start col-span-1 row-start-1 row-span-3">
          <AvatarSkeleton size={32} />
        </div>
      ) : (
        <TokenAvatar
          address={tokenAddress}
          chainId={chainId}
          size={32}
          className="col-span-1 row-start-1 row-span-3 mr-2"
        />
      )}
      <div
        className={fontStyles({
          variant: 'BODY_MD',
          class: [
            'text-token-gray-1600',
            'text-nowrap',
            'col-start-2',
            'col-end-3',
            'row-start-1',
            'row-end-2',
          ],
        })}
      >
        {' '}
        {loading ? (
          <TextSkeleton className="text-token-gray-400 flex items-center h-5.5" width={62} />
        ) : (
          <span>{title}</span>
        )}
      </div>

      <div
        className={fontStyles({
          variant: 'BODY_MD',
          class: [
            'text-token-gray-800',
            'text-nowrap',
            '-mt-[3px]',
            'col-start-2',
            'col-end-4',
            'row-start-2',
            'row-end-3',
          ],
        })}
      >
        {loading ? (
          <TextSkeleton className="text-token-gray-400 flex items-center h-5.5" width={40} />
        ) : (
          <span>{subtitle}</span>
        )}
      </div>

      {isNativeUsdc && (
        <div className="ml-2 flex flex-col h-full self-stretch">
          {loading ? <PillSkeleton height={24} width={84} /> : <IssuedByCircleBadge clickable />}
        </div>
      )}
    </Link>
  );
}
